"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { LandingConfig } from "@/types/site-config";

type DashboardSnapControllerProps = {
  config: LandingConfig;
  landing: React.ReactNode;
  dashboard: React.ReactNode;
};

export function DashboardSnapController({
  config,
  landing,
  dashboard,
}: DashboardSnapControllerProps) {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [dashboardLocked, setDashboardLocked] = useState(false);

  useEffect(() => {
    const dashboardNode = dashboardRef.current;
    if (!dashboardNode) return;

    const unlockToLanding = () => {
      setDashboardLocked(false);
      document.body.style.overflow = "";
      window.setTimeout(() => {
        document.getElementById("landing")?.scrollIntoView({ block: "start" });
      }, 0);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || dashboardLocked) return;
        setDashboardLocked(true);
        document.body.style.overflow = "hidden";
        if (window.location.hash !== "#dashboard") {
          window.history.pushState({ anarchyDashboard: true }, "", "#dashboard");
        }
        window.setTimeout(() => {
          dashboardNode.scrollIntoView({ block: "start" });
        }, 0);
      },
      { threshold: config.dashboardSnapThreshold },
    );

    const handlePopState = () => {
      unlockToLanding();
    };

    observer.observe(dashboardNode);
    window.addEventListener("popstate", handlePopState);

    return () => {
      observer.disconnect();
      window.removeEventListener("popstate", handlePopState);
      document.body.style.overflow = "";
    };
  }, [config.dashboardSnapThreshold, dashboardLocked]);

  return (
    <div className="snap-y snap-mandatory">
      <div className={cn("snap-start", dashboardLocked && "pointer-events-none opacity-0")}>
        {landing}
      </div>
      <div
        id="dashboard"
        ref={dashboardRef}
        className={cn(
          "snap-start bg-background",
          dashboardLocked && "fixed inset-0 z-40 h-screen overflow-y-auto",
        )}
      >
        {dashboard}
      </div>
    </div>
  );
}
